import { useState, useEffect } from "react";
import { CheckCompany, CheckForm, CheckFormParameter, CheckSignatory } from "winslow-core";
import "./AppEmployeeRecordCheckForm.css";
import { GenericSelect, GenericSelectOption } from "../../shared/GenericSelect";
import { Input } from "../../../ds2/components/Input";
import { Button } from "../../../ds2/components/Button";
import { Tooltip, TooltipContent } from "../../../ds2/components/Tooltip";
import { Checkbox } from "../../../ds2/components/Checkbox";
import { Info, AlertTriangle } from "lucide-react";
import { checkFormDefault, checkShouldIRenderFormParameter, checkParameterFormat } from "../app-payroll/checkFormDefaults";

// Complete List of Parameter Types

// 1. string - Accepts any type of string text input
//   - Used for text fields like names, addresses, SSN, etc.
// 2. number - Accepts positive numeric values (integers)
//   - Used for whole number inputs like dependents, allowances
// 3. currency - Accepts currency values with up to 2 decimal places
//   - Used for dollar amounts like additional withholding amounts
// 4. percent - Accepts positive decimal values from 0 to 100
//   - Used for percentage inputs like tax withholding percentages
// 5. select - Accepts values from a predefined list of options
//   - Used for dropdown/select fields with specific choices
//   - Requires an options array with value and label properties
// 6. boolean - Accepts boolean values (true/false)
//   - Typically rendered as Yes/No select options in the UI
//   - Used for binary choices like exemption status

// Implementation Example

// When building your form collection, each parameter will have this structure:

// {
//   "name": "parameter_name",
//   "label": "Display Label",
//   "description": "Optional help text",
//   "type": "string|number|currency|percent|select|boolean",
//   "options": null, // or array for select type
//   "required": true/false,
//   "depends_on": null // optional dependency
// }

type AppEmployeeRecordCheckFormProps = {
    disabled?: boolean; 
    checkForm: CheckForm;
    defaultValues?: Array<{ name: string; value: string }>;
    showTitle?: boolean;
    isSubmitted?: boolean;
    onSubmit?: (parameters: Array<{ name: string; value: string }>) => void;
}

export const AppEmployeeRecordCheckForm = ({
    disabled = false,
    checkForm,
    defaultValues,
    showTitle = false,
    isSubmitted = false,
    onSubmit} : AppEmployeeRecordCheckFormProps
) => {
    
    // State to store form values, keyed by parameter name
    const [formValues, setFormValues] = useState<Record<string, string>>({});
    const [signatureConfirmed, setSignatureConfirmed] = useState<boolean>(false);
    const [showAllParameters, setShowAllParameters] = useState<boolean>(false);
    
    // Reset signature confirmation when form changes
    useEffect(() => {
        setSignatureConfirmed(false);
    }, [checkForm?.id]);
    
    // Initialize form values from parameters (if they have a value property)
    // For required selects and booleans, use the first option as default
    useEffect(() => {
        const initialValues: Record<string, string> = {};
        if (checkForm?.parameters && Array.isArray(checkForm.parameters)) {
            checkForm.parameters.forEach((parameter: CheckFormParameter) => {
                // Check if parameter has a value property (may not be in type definition but could exist at runtime)
                const paramWithValue = parameter as CheckFormParameter & { value?: string };
                
                if (paramWithValue.value) {
                    // Use existing value if present
                    initialValues[parameter.name] = paramWithValue.value;
                } else if (defaultValues && defaultValues.find(dv => dv.name === parameter.name)) {
                    // Use default value if present
                    initialValues[parameter.name] = defaultValues.find(dv => dv.name === parameter.name)?.value || "";
                } else if (parameter.required) {
                    // Set defaults for required fields
                    if (parameter.type === "select" && parameter.options && Array.isArray(parameter.options) && parameter.options.length > 0) {
                        // For required selects, use first option as default
                        initialValues[parameter.name] = parameter.options[0].value;
                    } else if (parameter.type === "boolean") {
                        // For required booleans, default to "false" (No)
                        initialValues[parameter.name] = "false";
                    } else {
                        // For other required types, start with empty string
                        initialValues[parameter.name] = "";
                    }
                } else {
                    // Otherwise, start with empty string
                    initialValues[parameter.name] = "";
                }
            });
        }
        setFormValues(initialValues);
    }, [checkForm]);
    
    // Convert CheckFormParameterOption[] to GenericSelectOption[]
    // For non-required selects, add a "no selection" option
    const convertToGenericSelectOptions = (options: CheckFormParameter["options"], isRequired: boolean): GenericSelectOption[] => {
        if (!options || !Array.isArray(options)) {
            return isRequired ? [] : [{ displayName: "No selection", value: "" }];
        }
        
        const convertedOptions = options.map((option) => ({
            displayName: option.label,
            value: option.value,
        }));
        
        // Add "no selection" option for non-required selects
        if (!isRequired) {
            return [
                {
                    displayName: "No selection",
                    value: "",
                },
                ...convertedOptions
            ];
        }
        
        return convertedOptions;
    };
    
    // Get allowed characters from format patterns
    const getAllowedCharsFromFormats = (parameterName: string): string | null => {
        const formats = checkParameterFormat(parameterName);
        if (!formats || formats.length === 0) {
            return null; // No format restriction
        }
        
        // Collect all non-# characters from all formats
        const allowedChars = new Set<string>();
        formats.forEach(format => {
            format.split("").forEach(char => {
                if (char !== "#") {
                    allowedChars.add(char);
                }
            });
        });
        
        // Return regex pattern for allowed characters: digits + format separators
        return `[\\d${Array.from(allowedChars).map(c => c.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("")}]`;
    };

    // Convert format pattern (e.g., "###-####-#") to regex pattern
    const formatPatternToRegex = (format: string): RegExp => {
        // Replace # with \d (digit), escape other characters
        const regexPattern = format
            .split("")
            .map(char => char === "#" ? "\\d" : char.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
            .join("");
        return new RegExp(`^${regexPattern}$`);
    };

    // Check if value matches at least one format
    const matchesFormat = (parameterName: string, value: string): boolean => {
        const formats = checkParameterFormat(parameterName);
        if (!formats || formats.length === 0) {
            return true; // No format requirement, so it's valid
        }
        
        if (!value || value.trim() === "") {
            return true; // Empty values are valid (handled by required check)
        }
        
        // Trim the value before testing to handle accidental whitespace
        const trimmedValue = value.trim();
        
        const result = formats.some(format => {
            const regex = formatPatternToRegex(format);
            const matches = regex.test(trimmedValue);
            console.log(`[matchesFormat] Testing "${trimmedValue}" against format "${format}" (regex: ${regex}): ${matches}`);
            return matches;
        });
        
        console.log(`[matchesFormat] Parameter: "${parameterName}", Value: "${trimmedValue}", Formats:`, formats, `Result: ${result}`);
        
        return result;
    };

    // Get placeholder text from formats
    const getFormatPlaceholder = (parameterName: string): string | undefined => {
        const formats = checkParameterFormat(parameterName);
        if (!formats || formats.length === 0) {
            return undefined;
        }
        return formats.join(" or ");
    };
    
    // Validate field value based on type
    const isValidValue = (parameter: CheckFormParameter, value: string): boolean => {
        if (!value || value.trim() === "") {
            return false;
        }

        // Check format first if it exists
        if (!matchesFormat(parameter.name, value)) {
            return false;
        }

        switch (parameter.type) {
            case "number":
                const num = parseInt(value, 10);
                return !isNaN(num) && num >= 0;
            
            case "currency":
                const currency = parseFloat(value);
                return !isNaN(currency) && currency >= 0;
            
            case "percent":
                const percent = parseFloat(value);
                return !isNaN(percent) && percent >= 0 && percent <= 100;
            
            case "boolean":
                return value === "true" || value === "false";
            
            case "select":
            case "string":
            default:
                return value.trim() !== "";
        }
    };

    // Check if all required fields have values and formats are valid
    const canSubmit = (): boolean => {
        if (!checkForm?.parameters || !Array.isArray(checkForm.parameters)) {
            return false;
        }

        return checkForm.parameters.every((parameter: CheckFormParameter) => {
            const value = formValues[parameter.name] || "";
            
            // If field is empty, it's valid (unless required)
            if (!value || value.trim() === "") {
                if (checkShouldIRenderFormParameter(checkForm, parameter.name) && parameter.required) {
                    return false;
                }
                return true; // Empty non-required fields are valid
            }

            // If field has a value, it must match format (if format exists)
            if (!matchesFormat(parameter.name, value)) {
                return false;
            }

            // Also validate type
            if (checkShouldIRenderFormParameter(checkForm, parameter.name) && parameter.required) {
                return isValidValue(parameter, value);
            }

            return true;
        });
    };
    
    // Handle form submission
    const handleSubmit = () => {
        if (!onSubmit || !canSubmit() || !checkForm?.parameters || !Array.isArray(checkForm.parameters)) {
            return;
        }
        
        // Build array of {name, value} objects for parameters that have values
        const parametersWithValues = checkForm.parameters
            .filter((parameter: CheckFormParameter) => {
                const value = formValues[parameter.name] || "";
                return value.trim() !== ""; // Only include parameters with non-empty values
            })
            .map((parameter: CheckFormParameter) => ({
                name: parameter.name,
                value: formValues[parameter.name],
            }));
        
        onSubmit(parametersWithValues);
    };
    
    // Handle value change for select type
    const handleSelectChange = (parameterName: string, value: string) => {
        setFormValues(prev => ({
            ...prev,
            [parameterName]: value
        }));
    };
    
    // Handle value change for string type
    const handleInputChange = (parameterName: string, value: string) => {
        // Allow all typing - format validation happens on blur/submit, not on every keystroke
        setFormValues(prev => ({
            ...prev,
            [parameterName]: value
        }));
    };

    // Handle value change for number type (positive integers only)
    const handleNumberChange = (parameterName: string, value: string) => {
        // If there's a format requirement, allow all typing (including format chars like dashes)
        // Format validation happens on blur/submit, not on every keystroke
        const hasFormat = checkParameterFormat(parameterName);
        if (!hasFormat) {
            // No format restriction, remove any non-digit characters
            value = value.replace(/\D/g, "");
        }
        // If format exists, allow all typing - don't filter
        
        setFormValues(prev => ({
            ...prev,
            [parameterName]: value
        }));
    };

    // Handle value change for currency type (up to 2 decimal places)
    const handleCurrencyChange = (parameterName: string, value: string) => {
        // Remove any characters that aren't digits or decimal point
        let cleaned = value.replace(/[^\d.]/g, "");
        
        // Only allow one decimal point
        const parts = cleaned.split(".");
        if (parts.length > 2) {
            cleaned = parts[0] + "." + parts.slice(1).join("");
        }
        
        // Limit to 2 decimal places
        if (parts.length === 2 && parts[1].length > 2) {
            cleaned = parts[0] + "." + parts[1].substring(0, 2);
        }
        
        setFormValues(prev => ({
            ...prev,
            [parameterName]: cleaned
        }));
    };

    // Handle value change for percent type (0-100 with decimals)
    const handlePercentChange = (parameterName: string, value: string) => {
        // Remove any characters that aren't digits or decimal point
        let cleaned = value.replace(/[^\d.]/g, "");
        
        // Only allow one decimal point
        const parts = cleaned.split(".");
        if (parts.length > 2) {
            cleaned = parts[0] + "." + parts.slice(1).join("");
        }
        
        // Limit to 2 decimal places
        if (parts.length === 2 && parts[1].length > 2) {
            cleaned = parts[0] + "." + parts[1].substring(0, 2);
        }
        
        // Validate range (0-100)
        const numValue = parseFloat(cleaned);
        if (!isNaN(numValue) && numValue > 100) {
            cleaned = "100";
        }
        
        setFormValues(prev => ({
            ...prev,
            [parameterName]: cleaned
        }));
    };

    // Handle value change for boolean type (Yes/No)
    const handleBooleanChange = (parameterName: string, value: string) => {
        // Convert Yes/No to true/false
        const boolValue = value === "true" ? "true" : value === "false" ? "false" : "";
        setFormValues(prev => ({
            ...prev,
            [parameterName]: boolValue
        }));
    };

    // Create boolean options for GenericSelect
    const getBooleanOptions = (isRequired: boolean): GenericSelectOption[] => {
        const options: GenericSelectOption[] = [
            { displayName: "Yes", value: "true" },
            { displayName: "No", value: "false" }
        ];
        
        if (!isRequired) {
            return [
                { displayName: "No selection", value: "" },
                ...options
            ];
        }
        
        return options;
    };

    // console.log("[AppEmployeeRecordForm] checkForm: ", checkForm);
    
    if (!checkForm) {
        return null;
    }
    
    const parameters = checkForm.parameters || [];
    
    // Helper function to render label with info icon and tooltip
    const renderLabel = (parameter: CheckFormParameter) => {
        const value = formValues[parameter.name] || "";
        const hasFormatError = value && value.trim() !== "" && !matchesFormat(parameter.name, value);
        
        return (
            <label style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <span>{parameter.label}</span>
                {parameter.required && <span style={{ color: "red" }}>*</span>}
                {hasFormatError && (
                    <Tooltip>
                        {[
                            <AlertTriangle 
                                key="warning-icon"
                                size={14} 
                                style={{ 
                                    color: "#f59e0b", 
                                    cursor: "help",
                                    flexShrink: 0
                                }} 
                            />,
                            <TooltipContent key="tooltip-content">
                                Invalid format. Expected: {getFormatPlaceholder(parameter.name)}
                            </TooltipContent>
                        ]}
                    </Tooltip>
                )}
                {parameter.description && (
                    <Tooltip>
                        {[
                            <Info 
                                key="info-icon"
                                size={14} 
                                style={{ 
                                    color: "#666", 
                                    cursor: "help",
                                    flexShrink: 0
                                }} 
                            />,
                            <TooltipContent key="tooltip-content">
                                {parameter.description}
                            </TooltipContent>
                        ]}
                    </Tooltip>
                )}
            </label>
        );
    };
    
    // Filter parameters based on toggle state
    const filteredParameters = showAllParameters 
        ? parameters 
        : parameters.filter((parameter: CheckFormParameter) => 
            checkShouldIRenderFormParameter(checkForm, parameter.name)
        );

    console.log("[AppEmployeeRecordCheckForm] formValues: ", formValues);
    
    return (
        <div className="app-employee-record-form">  
            {showTitle && (
                <h3>{checkForm.description}</h3>
            )}
            <div style={{ marginBottom: "16px", display: "flex", alignItems: "center", gap: "8px" }}>
                <Checkbox
                    name="show-all-parameters"
                    choices={["Show all parameters"]}
                    choice={showAllParameters ? "Show all parameters" : undefined}
                    onChange={(choice) => setShowAllParameters(choice !== undefined)}
                    disabled={disabled}
                />
            </div>
            <div className="app-employee-record-form-fields">
                {filteredParameters.map((parameter: CheckFormParameter) => {
                    const currentValue = formValues[parameter.name] || "";
                    
                    // Render based on parameter type
                    if (parameter.type === "select") {
                        const selectOptions = convertToGenericSelectOptions(parameter.options || [], parameter.required || false);
                        const selectedOption = selectOptions.find(opt => opt.value === currentValue);
                        
                        return (
                            <div key={parameter.name} className="app-employee-record-form-field">
                                {renderLabel(parameter)}
                                <GenericSelect
                                    options={selectOptions}
                                    selected={selectedOption}
                                    onChange={(value: string) => handleSelectChange(parameter.name, value)}
                                    disabled={disabled}
                                />
                            </div>
                        );
                    }
                    
                    if (parameter.type === "boolean") {
                        const booleanOptions = getBooleanOptions(parameter.required || false);
                        const selectedOption = booleanOptions.find(opt => opt.value === currentValue);
                        
                        return (
                            <div key={parameter.name} className="app-employee-record-form-field">
                                {renderLabel(parameter)}
                                <GenericSelect
                                    options={booleanOptions}
                                    selected={selectedOption}
                                    onChange={(value: string) => handleBooleanChange(parameter.name, value)}
                                    disabled={disabled}
                                />
                            </div>
                        );
                    }
                    
                    if (parameter.type === "number") {
                        const formatPlaceholder = getFormatPlaceholder(parameter.name);
                        return (
                            <div key={parameter.name} className="app-employee-record-form-field">
                                {renderLabel(parameter)}
                                <Input
                                    type="text"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    value={currentValue}
                                    onChange={(e) => handleNumberChange(parameter.name, e.target.value)}
                                    disabled={disabled}
                                    required={parameter.required}
                                    placeholder={formatPlaceholder || "Enter a whole number"}
                                />
                            </div>
                        );
                    }
                    
                    if (parameter.type === "currency") {
                        const formatPlaceholder = getFormatPlaceholder(parameter.name);
                        return (
                            <div key={parameter.name} className="app-employee-record-form-field">
                                {renderLabel(parameter)}
                                <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
                                    <span style={{ position: "absolute", left: "8px", color: "#666" }}>$</span>
                                    <Input
                                        type="text"
                                        inputMode="decimal"
                                        value={currentValue}
                                        onChange={(e) => handleCurrencyChange(parameter.name, e.target.value)}
                                        disabled={disabled}
                                        required={parameter.required}
                                        placeholder={formatPlaceholder || "0.00"}
                                        style={{ paddingLeft: "24px" }}
                                    />
                                </div>
                            </div>
                        );
                    }
                    
                    if (parameter.type === "percent") {
                        const formatPlaceholder = getFormatPlaceholder(parameter.name);
                        return (
                            <div key={parameter.name} className="app-employee-record-form-field">
                                {renderLabel(parameter)}
                                <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
                                    <Input
                                        type="text"
                                        inputMode="decimal"
                                        value={currentValue}
                                        onChange={(e) => handlePercentChange(parameter.name, e.target.value)}
                                        disabled={disabled}
                                        required={parameter.required}
                                        placeholder={formatPlaceholder || "0.00"}
                                        style={{ paddingRight: "32px" }}
                                    />
                                    <span style={{ position: "absolute", right: "8px", color: "#666" }}>%</span>
                                </div>
                            </div>
                        );
                    }
                    
                    // Default to string type
                    const formatPlaceholder = getFormatPlaceholder(parameter.name);
                    return (
                        <div key={parameter.name} className="app-employee-record-form-field">
                            {renderLabel(parameter)}
                            <Input
                                type="text"
                                value={currentValue}
                                onChange={(e) => handleInputChange(parameter.name, e.target.value)}
                                disabled={disabled}
                                required={parameter.required}
                                placeholder={formatPlaceholder}
                            />
                        </div>
                    );
                })}
            </div>
            {onSubmit && (
                <div style={{ marginTop: "24px" }}>
                    {(checkForm as any).signature_confirmation_text && (
                        <div style={{ marginBottom: "16px" }}>
                            <Checkbox
                                name="signature-confirmation"
                                choices={[(checkForm as any).signature_confirmation_text]}
                                choice={signatureConfirmed ? (checkForm as any).signature_confirmation_text : undefined}
                                onChange={(choice) => setSignatureConfirmed(choice !== undefined)}
                                disabled={disabled}
                            />
                        </div>
                    )}
                    <div style={{ display: "flex", justifyContent: "flex-end" }}>
                        <Button
                            label={isSubmitted ? "Update" : "Submit"}
                            variant="primary"
                            onClick={handleSubmit}
                            disabled={disabled || !canSubmit() || (!signatureConfirmed && (checkForm as any).signature_confirmation_text)}
                        />
                    </div>
                </div>
            )}
        </div>
    )
}